import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect } from "react";
import { css } from "styled-components";

const enum fields {
    hubspotScriptRegion = "hubspotScriptRegion",
    hubspotScriptPortalId = "hubspotScriptPortalId",
    hubspotScriptFormId = "hubspotScriptFormId",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.hubspotScriptRegion]: string;
        [fields.hubspotScriptPortalId]: string;
        [fields.hubspotScriptFormId]: string;
    };
}

export interface HubspotFormStyles {
    hubspotFormStyles: InjectableCss;
}

const hubspotFormStyles: HubspotFormStyles = {
    hubspotFormStyles: {
        css: css`
            padding-bottom: 50px;
            padding-top: 50px;
            display: block;

            .input {
                margin: 0 !important;
            }

            form {
                width: 100% !important;
                font-family: Lato, Arial, Helvetica, sans-serif;
            }

            label {
                display: inline-block;
                margin-bottom: 10px;
                font-weight: 600;
                letter-spacing: 0.57px;
                line-height: 24px;
            }

            label > span {
                color: ${getColor("text.main")};
            }

            .form-columns-2 .hs-form-field:nth-child(odd) {
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                padding-right: none;
                            `,
                            null,
                            null,
                            css`
                                padding-right: 0.5rem;
                            `,
                        ],
                        "min",
                    )}
            }

            .form-columns-2 .hs-form-field:nth-child(even) {
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                padding-left: none;
                            `,
                            null,
                            null,
                            css`
                                padding-left: 0.5rem;
                            `,
                        ],
                        "min",
                    )}
            }

            .hs-form-required {
                color: ${getColor("danger.main")};
            }

            .hs-error-msg {
                color: ${getColor("danger.main")};
                font-size: 0.7rem !important;
                margin: 0;
                padding: 0;
            }

            .hs-form-booleancheckbox a {
                color: ${getColor("text.link")};
            }

            .hs-form-field {
                padding: 1rem 0;

                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            css`
                                width: 100% !important;
                                display: block;
                            `,
                            null,
                        ],
                        "max",
                    )}
            }

            .inputs-list {
                display: flex;
            }

            label.hs-form-radio-display {
                padding: 0 2rem 0 0;

                span {
                    margin-left: 0.5rem;
                }
            }

            label.hs-form-booleancheckbox-display {
                display: flex;
            }

            input[type="radio"],
            input[type="checkbox"],
            input[type="submit"] {
                all: revert;
            }

            input[type="radio"],
            input[type="checkbox"] {
                width: auto;
                filter: saturate(3.5) hue-rotate(159deg);
                transform: scale(1.5);
            }

            .hs-phone {
                width: 50%;
                padding-right: 0.5rem;

                .input {
                    margin-right: 0;
                }

                input {
                    width: 100% !important;
                }
            }

            fieldset {
                display: block !important;
                max-width: none !important;
                border: 0;
                padding: 0;
                margin: 0;
                width: 100%;
                display: contents;
            }

            input,
            select {
                width: 100%;
                box-sizing: border-box;
                font-family: inherit;
                font-size: 15px;
                padding: 0 10px 0 10px;
                height: 50px;
                background: ${getColor("common.accent")};
                border: 1px solid ${getColor("common.border")};
                border-radius: 0;
                border-width: 1px !important;
                color: ${getColor("secondary.main")};
                padding-left: 10px;
                padding-right: 75px;
                text-overflow: ellipsis;
            }

            textarea.hs-input {
                min-height: 300px !important;
                width: 100% !important;
            }

            input.hs-button.primary.large {
                cursor: pointer;
                font-family: inherit;
                font-weight: bold;
                backface-visibility: hidden;
                word-break: keep-all;
                border: none;
                border-radius: 0;
                position: relative;
                transition: transform 0.25s ease-in-out, background 0.25s ease-in-out;
                padding: 15px;
                height: auto;
                min-height: auto;
                line-height: 1.2;
                background-color: ${getColor("primary.main")};
                min-width: 150px;

                :hover {
                    background: hsl(44.10000000000002, 100%, 41%);
                    border-color: hsl(44.10000000000002, 100%, 41%);
                }
            }
        `,
    },
};

declare global {
    interface Window {
        hbspt: {
            forms: {
                create: any;
            };
        };
    }
}

const HubspotForm = ({ fields }: OwnProps) => {
    const { hubspotScriptRegion, hubspotScriptPortalId, hubspotScriptFormId } = fields;
    const styles = hubspotFormStyles;

    useEffect(() => {
        const script = document.createElement("script");
        const tracker = document.createElement("script");

        script.src = "https://js.hsforms.net/forms/v2.js";
        tracker.src = `//js.hs-scripts.com/${hubspotScriptPortalId}.js`;

        document.body.appendChild(tracker);
        document.body.appendChild(script);

        script.addEventListener("load", () => {
            if (window.hbspt) {
                window.hbspt.forms.create({
                    region: hubspotScriptRegion,
                    portalId: hubspotScriptPortalId,
                    formId: hubspotScriptFormId,
                    target: "#hubspotForm",
                });
            }
        });
    }, []);

    return (
        <StyledWrapper {...styles.hubspotFormStyles}>
            <div id="hubspotForm"></div>
        </StyledWrapper>
    );
};

const hubspotForm: WidgetModule = {
    component: HubspotForm,
    definition: {
        displayName: "HubSpot Form",
        group: "Common",
        fieldDefinitions: [
            {
                name: fields.hubspotScriptRegion,
                editorTemplate: "TextField",
                displayName: "Hubspot Script Region",
                defaultValue: "Script Region",
                fieldType: "Translatable",
            },
            {
                name: fields.hubspotScriptPortalId,
                editorTemplate: "TextField",
                displayName: "Hubspot Script PortalId",
                defaultValue: "Script PortalId",
                fieldType: "Translatable",
            },
            {
                name: fields.hubspotScriptFormId,
                editorTemplate: "TextField",
                displayName: "Hubspot Script FormId",
                defaultValue: "Script FormId",
                fieldType: "Translatable",
            },
        ],
    },
};

export default hubspotForm;
